import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';

import { Observable } from 'rxjs';

import { Document } from 'modules/shared/models/document.models';
import { DocumentResponse, CreateOrUpdateRecordDocumentRequest } from 'store/models/document.models';

import { environment } from 'environment/environment';

export interface IDocumentsRequest {
  limit?: number;
  offset?: number;
  search?: string;
  sort?: string;
  expiry_date?: any;
  expiring?: boolean;
  show_expiry_alerts?: boolean;
  expiry_date__lte?: any;
}

@Injectable()
export class DocumentsService {

  readonly apiBase: string = environment.API_BASE + '/api/documents/';

  constructor(private httpClient: HttpClient) { }

  /**
   * Get list of expiring documents
   * @method getDocuments
   */
  getDocuments(documentsReqBody?: IDocumentsRequest): Observable<DocumentResponse> {
    let params: HttpParams = new HttpParams();

    if (documentsReqBody) {
      const reqBodyEntries: Array<[string, any]> = Object.entries(documentsReqBody);

      reqBodyEntries.forEach(([key, value]: [string, any]) => {
        if (value !== undefined) {
          params = params.append(key, value);
        }
      });
    }

    return this.httpClient.get<DocumentResponse>(this.apiBase, { params: params });
  }

  /**
   * Toggle notification about document expiry
   * @method toggleNotification
   */
  toggleNotification(documentId: number, showAlert: boolean): Observable<Document> {
    const url: string = `${this.apiBase}${documentId}/`;
    return this.httpClient.patch<Document>(url, { showExpiryAlerts: showAlert });
  }
  /**
   * Get list of authenticites
   * @method getAuthenticites
   */
  getAuthenticites(): Observable<Array<string>> {
    const url: string = `${this.apiBase}authenticities/`;
    return this.httpClient.get<Array<string>>(url);
  }

  /**
   * Get list of document types
   * @method getDocumentTypes
   */
  getDocumentTypes(recordType?: string): Observable<Array<string>> {
    const url: string = `${this.apiBase}document_types/?filter=${recordType}`;
    return this.httpClient.get<Array<string>>(url);
  }

  createRecordDocument(payload: CreateOrUpdateRecordDocumentRequest): Observable<Document> {
    const form = new FormData();
    if (payload.document) {
      form.append('document', payload.document);
    }
    if (payload.backDocument) {
      form.append('back_document', payload.backDocument);
    }
    if (payload.documentNumber) {
      form.append('document_number', payload.documentNumber);
    }
    if (payload.authenticity) {
      form.append('authenticity', payload.authenticity);
    }
    if (payload.docType) {
      form.append('doc_type', payload.docType);
    }
    if (payload.docTypeCategory) {
      form.append('doc_type_category', payload.docTypeCategory);
    }
    if (payload.issueDate) {
      form.append('issue_date', payload.issueDate);
    }
    if (payload.expiryDate) {
      form.append('expiry_date', payload.expiryDate);
    }
    if (payload.showExpiryAlerts != null) {
      form.append('show_expiry_alerts', String(payload.showExpiryAlerts));
    }
    return this.httpClient.post<Document>(`${environment.API_BASE}/api/records/${payload.recordId}/documents/`, form);
  }

  updateRecordDocument(payload: CreateOrUpdateRecordDocumentRequest): Observable<Document> {
    const form = new FormData();
    if (payload.document) {
      form.append('document', payload.document);
    }
    if (payload.backDocument) {
      form.append('back_document', payload.backDocument);
    } else if (payload.backDocument === null) {
      form.append('back_document', '');
    }
    if (payload.documentNumber) {
      form.append('document_number', payload.documentNumber);
    }
    if (payload.authenticity) {
      form.append('authenticity', payload.authenticity);
    }
    if (payload.docType) {
      form.append('doc_type', payload.docType);
    }
    if (payload.docTypeCategory) {
      form.append('doc_type_category', payload.docTypeCategory);
    }
    if (payload.issueDate) {
      form.append('issue_date', payload.issueDate);
    }
    if (payload.expiryDate) {
      form.append('expiry_date', payload.expiryDate);
    }
    if (payload.showExpiryAlerts != null) {
      form.append('show_expiry_alerts', String(payload.showExpiryAlerts));
    }

    return this.httpClient.patch<Document>(`${environment.API_BASE}/api/documents/${payload.id}/`, form);
  }

  deleteDocument(documentId: number): Observable<any> {
    return this.httpClient.delete(`${this.apiBase}${documentId}/`);
  }
}
